import { Component, computed, inject } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { EntityService } from '../../services/entity.service';
import { CHANGE_OP_TEXT, ChangesPage, madeWhen } from '../../services/offline/changes-page';
import { SignOutStop, SignOutStopChoice, signOutStopCopy } from '../../services/offline/sign-out-stop';
import { UNSAVED_REASON_TEXT, WaitingChange } from '../../services/offline/waiting-changes-store';

/** "farm_plots" reads as "Farm plots": the person never sees an id or an underscore. */
function readable(name: string): string {
  const words = name.replace(/_/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : words;
}

/**
 * The changes page: what this phone is still to send, and what Zavo turned down. Plain and neutral on
 * purpose: nothing failed that the person must fix, so no error colours, and no Remove or Retry, so their
 * own work cannot be thrown away from here. When the sign-out stop asks, it sits at the top, so the person
 * sees exactly what signing out would remove. Runtime builds only.
 *
 * Under the lock, stop and access screens (z-200), which are final; over the app and its banner.
 */
@Component({
  selector: 'app-changes-page',
  standalone: true,
  imports: [NgIf, NgFor],
  template: `
    <div *ngIf="page.view() as v" data-changes-page [attr.role]="asking() ? 'alertdialog' : 'dialog'" aria-modal="true"
         aria-labelledby="changes-page-title"
         class="fixed inset-0 z-[199] flex flex-col overflow-y-auto bg-white text-[#332D2C] dark:bg-gray-900 dark:text-gray-100">
      <div class="mx-auto flex w-full max-w-md flex-col gap-6 p-6">
        <section *ngIf="asking() as q" class="flex flex-col items-center gap-2 pt-6 text-center">
          <h1 id="changes-page-title" class="text-lg font-semibold">{{ q.title }}</h1>
          <p class="max-w-xs text-sm text-gray-500 dark:text-gray-400">{{ q.detail }}</p>
          <!-- Was (2026-10-24):
          <div class="mt-4 flex gap-3">
            <button type="button" (click)="answer('go-back')"
                    class="rounded-lg bg-gray-800 px-5 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900">Go back</button>
            <button type="button" (click)="answer('sign-out-anyway')"
                    class="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-[#332D2C] dark:border-gray-600 dark:text-gray-100">Sign out anyway</button>
          </div>
          -->
          <!-- "Confirm sign out" names the step that loses the waiting changes, so it can't be tapped as a plain "yes". -->
          <div class="mt-4 flex gap-3">
            <button type="button" (click)="answer('go-back')"
                    class="rounded-lg bg-gray-800 px-5 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900">Return</button>
            <button type="button" (click)="answer('sign-out-anyway')"
                    class="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-[#332D2C] dark:border-gray-600 dark:text-gray-100">Confirm sign out</button>
          </div>
        </section>

        <div *ngIf="!asking()" class="flex items-center justify-between">
          <h1 id="changes-page-title" class="text-lg font-semibold">Your changes</h1>
          <button type="button" (click)="page.close()"
                  class="rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-[#332D2C] dark:border-gray-600 dark:text-gray-100">Close</button>
        </div>

        <section *ngIf="v.unsaved.length" data-unsaved class="flex flex-col gap-2">
          <h2 class="text-sm font-semibold">Not saved</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">Zavo could not save these. If they still matter, make them again.</p>
          <ul class="flex flex-col divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            <li *ngFor="let c of v.unsaved; trackBy: byId" class="flex flex-col gap-0.5 px-4 py-3">
              <span class="text-sm font-medium">{{ label(c) }}</span>
              <span class="text-xs text-gray-500 dark:text-gray-400">{{ done(c) }}</span>
              <span class="text-sm text-gray-700 dark:text-gray-300">{{ reason(c) }}</span>
            </li>
          </ul>
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="text-sm font-semibold">Waiting to be sent</h2>
          <p *ngIf="!v.waiting.length" class="text-sm text-gray-500 dark:text-gray-400">Nothing is waiting to be sent.</p>
          <ul *ngIf="v.waiting.length" data-waiting
              class="flex flex-col divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            <li *ngFor="let c of v.waiting; trackBy: byId" class="flex flex-col gap-0.5 px-4 py-3">
              <span class="text-sm font-medium">{{ label(c) }}</span>
              <span class="text-xs text-gray-500 dark:text-gray-400">{{ done(c) }}</span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  `,
})
export class ChangesPageComponent {
  readonly page = inject(ChangesPage);
  private readonly stop = inject(SignOutStop);
  private readonly entities = toSignal(inject(EntityService).$values, { initialValue: [] });
  private readonly names = computed(() => new Map(this.entities().map((e) => [e.id, e.name])));

  readonly asking = computed(() => {
    const q = this.stop.asking();
    return q ? signOutStopCopy(q) : null;
  });

  /** The entity's name; before the entities load, its id without the provisioning suffix. */
  label(c: WaitingChange): string {
    return readable(this.names().get(c.entityId) ?? c.entityId.replace(/_[A-Za-z0-9]{6}$/, ''));
  }
  done(c: WaitingChange): string { return `${CHANGE_OP_TEXT[c.op]} · ${madeWhen(c)}`; }
  reason(c: WaitingChange): string { return c.unsaved ? UNSAVED_REASON_TEXT[c.unsaved.reason] : ''; }
  byId(_: number, c: WaitingChange): string { return c.changeId; }
  answer(choice: SignOutStopChoice): void { this.stop.answer(choice); }
}
